import React from 'react';
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import { Box, Typography, CircularProgress } from "@mui/material";
import axios from 'axios';
import getCookie from '../../../utils/getCookies';
import { DateTime } from 'luxon';


function FindSubstituteButton({
    event,
    colors,
    handlePopOverClose,
    enqueueSnackbar,
}) {
    const [open, setOpen] = React.useState(false);
    const [loadingAction, setLoadingAction] = React.useState(null); // null | "preview" | "send"
    // null = initial, "preview" = preview results shown, "sent" = emails queued
    const [stage, setStage] = React.useState(null);
    const [previewData, setPreviewData] = React.useState(null);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setStage(null);
        setPreviewData(null);
        handlePopOverClose();
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    const formatDateTime = (isoString) => {
        if (!isoString) return "N/A";
        return DateTime.fromISO(isoString).setZone('America/New_York').toFormat('MMM d, yyyy h:mm a');
    };

    const callApi = async (dryRun) => {
        const token = getCookie("_auth");
        const response = await axios.post(
            `${process.env.REACT_APP_URL}/api/v1/mails/courses/${event.course.uuid}/find-substitute`,
            {
                event_title: event.title,
                event_start: event.start,
                event_end: event.end,
                dry_run: dryRun,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data;
    };

    const handlePreview = async () => {
        setLoadingAction("preview");
        try {
            const data = await callApi(true);
            setPreviewData(data);
            setStage("preview");
            if (data.count === 0) {
                enqueueSnackbar("No matching substitute teachers found", { variant: "warning" });
            }
        } catch (error) {
            console.error(error);
            const message = error.response?.data?.error || "Failed to load preview";
            enqueueSnackbar(message, { variant: "error" });
        } finally {
            setLoadingAction(null);
        }
    };

    const handleSend = async () => {
        setLoadingAction("send");
        try {
            const data = await callApi(false);
            setPreviewData(data);
            setStage("sent");
            if (data.count > 0) {
                enqueueSnackbar(`Substitute request sent to ${data.count} teacher(s)`, { variant: "success" });
            }
        } catch (error) {
            console.error(error);
            const message = error.response?.data?.error || "Failed to send substitute request emails";
            enqueueSnackbar(message, { variant: "error" });
        } finally {
            setLoadingAction(null);
        }
    };

    const teachingSubjects = event.course?.teaching_subject
        ? event.course.teaching_subject.split("|").map(s => s.trim()).filter(Boolean).join(", ")
        : null;

    return (
        <div>
            <Button
                onClick={handleOpen}
                sx={{
                    p: 1,
                    width: "100%",
                    borderRadius: "0px",
                    bgcolor: colors.purpleAccent[900],
                    '&:hover': {
                        bgcolor: colors.purpleAccent[800],
                    },
                }}
            >
                Find Substitute
            </Button>
            <Modal
                aria-labelledby="find-substitute-modal-title"
                aria-describedby="find-substitute-modal-description"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Typography variant="h4" color={colors.primary[450]} mb={2}>
                            Find Substitute Teacher
                        </Typography>

                        {/* Initial view */}
                        {stage === null && (
                            <Box display="flex" flexDirection="column" gap={2} pt={1}>
                                <Typography variant="body1">
                                    <strong>Course:</strong> {event.course?.course_title || "N/A"}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Class:</strong> {event.title || "N/A"}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Date/Time:</strong> {formatDateTime(event.start)} - {formatDateTime(event.end)}
                                </Typography>
                                {teachingSubjects ? (
                                    <Typography variant="body1">
                                        <strong>Matching Subjects:</strong> {teachingSubjects}
                                    </Typography>
                                ) : (
                                    <Typography variant="body1" color="warning.main">
                                        No teaching subjects configured for this course. No teachers will be matched.
                                    </Typography>
                                )}

                                <Typography variant="body2" color={colors.grey[300]} mt={1}>
                                    This will find all active teachers whose subject matches this course's teaching subject, excluding currently assigned teachers.
                                </Typography>

                                <Box display="flex" gap={2} mt={2}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSend}
                                        disabled={loadingAction !== null || !teachingSubjects}
                                        sx={{ minWidth: "10rem" }}
                                    >
                                        {loadingAction === "send" ? <CircularProgress size={24} /> : "Send Emails"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="info"
                                        onClick={handlePreview}
                                        disabled={loadingAction !== null || !teachingSubjects}
                                    >
                                        {loadingAction === "preview" ? <CircularProgress size={24} /> : "Preview"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleClose}
                                        disabled={loadingAction !== null}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* Preview result */}
                        {stage === "preview" && previewData && (
                            <Box display="flex" flexDirection="column" gap={2} pt={1}>
                                <Typography variant="h5" color={previewData.count > 0 ? "info.main" : "warning.main"}>
                                    {previewData.count > 0
                                        ? `${previewData.count} teacher(s) matched`
                                        : "No matching substitute teachers found"
                                    }
                                </Typography>

                                {previewData.teachers_emailed && previewData.teachers_emailed.length > 0 && (
                                    <Box>
                                        <Typography variant="body1" mb={1}><strong>Teachers:</strong></Typography>
                                        {previewData.teachers_emailed.map((t, i) => (
                                            <Typography key={i} variant="body2" sx={{ pl: 2 }}>
                                                {t.name} ({t.email})
                                            </Typography>
                                        ))}
                                    </Box>
                                )}

                                {previewData.teachers_excluded && previewData.teachers_excluded.length > 0 && (
                                    <Box>
                                        <Typography variant="body1" mb={1} color="warning.main">
                                            <strong>Excluded (have class that day):</strong>
                                        </Typography>
                                        {previewData.teachers_excluded.map((t, i) => (
                                            <Typography key={i} variant="body2" sx={{ pl: 2 }} color={colors.grey[300]}>
                                                {t.name} ({t.email})
                                            </Typography>
                                        ))}
                                    </Box>
                                )}

                                {previewData.email_subject && (
                                    <Typography variant="body2" color={colors.grey[300]} mt={1}>
                                        <strong>Email Subject:</strong> {previewData.email_subject}
                                    </Typography>
                                )}

                                {previewData.email_html && (
                                    <Box mt={1}>
                                        <Typography variant="body2" mb={1}><strong>Email Preview:</strong></Typography>
                                        <Box
                                            sx={{
                                                border: `1px solid ${colors.grey[700]}`,
                                                borderRadius: 1,
                                                bgcolor: '#fff',
                                                maxHeight: 300,
                                                overflow: 'auto',
                                            }}
                                            dangerouslySetInnerHTML={{ __html: previewData.email_html }}
                                        />
                                    </Box>
                                )}

                                <Box display="flex" gap={2} mt={2}>
                                    {previewData.count > 0 && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSend}
                                            disabled={loadingAction !== null}
                                            sx={{ minWidth: "10rem" }}
                                        >
                                            {loadingAction === "send" ? <CircularProgress size={24} /> : "Send Emails"}
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        onClick={handleClose}
                                        disabled={loadingAction !== null}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* Sent confirmation */}
                        {stage === "sent" && previewData && (
                            <Box display="flex" flexDirection="column" gap={2} pt={1}>
                                <Typography variant="h5" color="success.main">
                                    Emails queued for {previewData.count} teacher(s)
                                </Typography>

                                {previewData.teachers_emailed && previewData.teachers_emailed.length > 0 && (
                                    <Box>
                                        <Typography variant="body1" mb={1}><strong>Teachers emailed:</strong></Typography>
                                        {previewData.teachers_emailed.map((t, i) => (
                                            <Typography key={i} variant="body2" sx={{ pl: 2 }}>
                                                {t.name} ({t.email})
                                            </Typography>
                                        ))}
                                    </Box>
                                )}

                                <Button
                                    variant="contained"
                                    onClick={handleClose}
                                    sx={{ width: "7rem", mt: 2 }}
                                >
                                    Close
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
}

export default FindSubstituteButton;
